import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';
import type { ConfigValueItem, ConfigValueDetail } from '@services/adminConsoleService';

interface ReportConfig {
  Id: number;
  ReportTypeId: number;
  ReportTypeName: string;
  ConfigValueID: number;
  ItemType: string;
  LayerId: number;
  LayerName: string;
  CustomFieldId: number;
  CustomField: string;
}

interface ReportType {
  id: number;
  name: string;
}

// Known report types (no dedicated API endpoint)
const REPORT_TYPES: ReportType[] = [
  { id: 0, name: 'Time tracking Report' },
  { id: 1, name: 'Availability Report' },
  { id: 2, name: 'Efficiency Report' },
  { id: 3, name: 'Captured Time Report' },
  { id: 4, name: 'PTO Report' },
  { id: 5, name: 'Attendance Report' },
];

const EMPTY_FORM = {
  reportTypeId: null as number | null,
  configValueId: null as number | null,
  itemType: '',
  layerId: null as number | null,
  layerName: '',
  customFieldId: null as number | null,
  customField: '',
  reportTypeName: '',
};

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

interface ConfigModalProps {
  title: string;
  form: typeof EMPTY_FORM;
  itemTypes: ConfigValueItem[];
  detail: ConfigValueDetail | null;
  detailLoading: boolean;
  saving: boolean;
  error: string | null;
  onReportTypeChange: (id: number, name: string) => void;
  onItemTypeChange: (id: number, name: string) => void;
  onLayerChange: (id: number, name: string) => void;
  onFormChange: (patch: Partial<typeof EMPTY_FORM>) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const ConfigModal: React.FC<ConfigModalProps> = ({
  title,
  form,
  itemTypes: _itemTypes,
  detail,
  detailLoading: _detailLoading,
  saving,
  error,
  onReportTypeChange,
  onItemTypeChange: _onItemTypeChange,
  onLayerChange: _onLayerChange,
  onFormChange,
  onConfirm,
  onClose,
}) =>
{
  const customFields = useMemo(() =>
  {
    if (!detail) return [];
    // return detail.Layers.find((l) => l.LayerId === form.layerId)?.CustomFields ?? [];
    return detail.Layers.find(l => l.Name === 'Internal')?.CustomFields ?? [];
  }, [detail, form.layerId]);

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[500px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">{title}</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828]">
              {error}
            </div>
          )}

          {/* Report Type */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Report Type:</label>
            <select
              value={form.reportTypeId ?? ''}
              onChange={e =>
                onReportTypeChange(Number(e.target.value), e.target.selectedOptions[0]?.text ?? '')
              }
              className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] bg-white"
            >
              <option value="">— Select Report Type —</option>
              {REPORT_TYPES.map(rt => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </select>
          </div>
          {/* Custom Detail */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Custom Detail:</label>
            <select
              value={form.customFieldId ?? ''}
              // disabled={!hasCF}
              onChange={e =>
              {
                const cf = customFields.find(c => c.CustomFieldId === Number(e.target.value));
                if (cf)
                  onFormChange({
                    customFieldId: cf.CustomFieldId,
                    customField: cf.Name,
                    layerId: cf.LayerId,
                    configValueId: 15,
                    layerName: 'Internal',
                    itemType: detail?.SideMenuText,
                  });
              }}
              className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb] bg-white disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
            >
              <option value="">— Select Custom Detail —</option>
              {customFields.map(cf => (
                <option key={cf.CustomFieldId} value={cf.CustomFieldId}>
                  {cf.Name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] transition-colors disabled:opacity-50 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

interface DeleteModalProps {
  config: ReportConfig;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ config, saving, error, onConfirm, onClose }) =>
  createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[420px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Delete Report Configuration</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828]">
              {error}
            </div>
          )}
          <p className="text-[12px] text-[#555]">
            Are you sure you want to delete this report configuration?
          </p>
          <div className="flex flex-col gap-2">
            {[
              { label: 'Report Type', value: config.ReportTypeName },
              // { label: 'Item Type',   value: config.ItemType },
              // { label: 'Layer',       value: config.LayerName },
              { label: 'Custom Detail', value: config.CustomField },
            ].map(({ label, value }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <span className="text-[11px] text-[#A5A7AC]">{label}:</span>
                <div className="bg-[#f5f5f5] border border-[#e0e0e0] rounded px-3 py-1.5 text-[12px] text-[#555]">
                  {value || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#e57373] text-white rounded hover:bg-[#d32f2f] transition-colors disabled:opacity-50"
          >
            {saving ? '…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const ReportConfigTab: React.FC = () =>
{
  const [configs, setConfigs] = useState<ReportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Item types (for dropdowns)
  const [itemTypes, _setItemTypes] = useState<ConfigValueItem[]>([]);

  // Detail (layers + custom fields) for selected item type
  const [detail, setDetail] = useState<ConfigValueDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal
  type ModalMode = 'add' | 'edit' | 'delete';
  const [modal, setModal] = useState<ModalMode | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportConfig | null>(null);
  const [form, setForm] = useState<typeof EMPTY_FORM>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const loadConfigs = useCallback(async () =>
  {
    try
    {
      const data = await adminConsoleService.getReportConfigurations();
      setConfigs(data);
    }
    catch
    {
      // non-fatal
    }
    finally
    {
      setLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    loadConfigs();
    loadDetail(15);

    // adminConsoleService.getConfigValueAll()
    //   .then(setItemTypes)
    //   .catch(() => {});
  }, [loadConfigs]);

  const loadDetail = useCallback(async (configValueId: number) =>
  {
    setDetailLoading(true);
    setDetail(null);
    try
    {
      const d = await adminConsoleService.getConfigValueById(configValueId);
      setDetail(d);
      setForm(prev => ({
        ...prev,
        layerId: d.Layers.find(x => x.Name === 'Internal')?.LayerId ?? 0,
      }));
    }
    catch
    {
      // ignore
    }
    finally
    {
      setDetailLoading(false);
    }
  }, []);

  const filtered = useMemo(() =>
  {
    const q = search.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter(
      c =>
        c.ReportTypeName.toLowerCase().includes(q) ||
        c.ItemType.toLowerCase().includes(q) ||
        c.LayerName.toLowerCase().includes(q) ||
        c.CustomField.toLowerCase().includes(q),
    );
  }, [configs, search]);

  const openAdd = () =>
  {
    setForm({ ...EMPTY_FORM });
    // setDetail(null);
    setModalError(null);
    setEditingId(null);
    setModal('add');
  };

  const openEdit = (c: ReportConfig) =>
  {
    setForm({
      reportTypeId: c.ReportTypeId,
      configValueId: c.ConfigValueID,
      itemType: c.ItemType,
      layerId: c.LayerId,
      layerName: c.LayerName,
      customFieldId: c.CustomFieldId,
      customField: c.CustomField,
      reportTypeName: c.ReportTypeName,
    });
    // setDetail(null);
    setModalError(null);
    setEditingId(c.Id);
    setModal('edit');
    //if (c.ConfigValueID) loadDetail(c.ConfigValueID);
  };

  const openDelete = (c: ReportConfig) =>
  {
    setDeleteTarget(c);
    setModalError(null);
    setModal('delete');
  };

  const closeModal = () =>
  {
    setModal(null);
    setDeleteTarget(null);
    setEditingId(null);
    setModalError(null);
  };

  const handleItemTypeChange = (id: number, name: string) =>
  {
    setForm(prev => ({
      ...prev,
      configValueId: id,
      itemType: name,
      layerId: null,
      layerName: '',
      customFieldId: null,
      customField: '',
    }));
    loadDetail(id);
  };

  const handleLayerChange = (id: number, name: string) =>
  {
    setForm(prev => ({
      ...prev,
      layerId: id,
      layerName: name,
      customFieldId: null,
      customField: '',
    }));
  };

  const handleConfirm = async () =>
  {
    if (form.reportTypeId === null)
    {
      setModalError('Report Type is required.');
      return;
    }
    if (!form.configValueId)
    {
      setModalError('Item Type is required.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try
    {
      if (modal == 'add')
      {
        if (isDuplicated(form))
        {
          setModalError('Report Configuration already exist.');
          return;
        }
        await adminConsoleService.addReportConfiguration({
          ReportTypeId: form.reportTypeId ?? 0,
          ConfigValueID: form.configValueId ?? 0,
          ItemType: form.itemType,
          LayerId: form.layerId ?? 0,
          LayerName: form.layerName,
          CustomFieldId: form.customFieldId ?? 0,
          CustomField: form.customField,
          ReportTypeName: form.reportTypeName,
        });
      }
      else
      {
        await adminConsoleService.updateReportConfiguration({
          Id: editingId,
          ReportTypeId: form.reportTypeId ?? 0,
          ConfigValueID: form.configValueId ?? 0,
          ItemType: form.itemType,
          LayerId: form.layerId ?? 0,
          LayerName: form.layerName,
          CustomFieldId: form.customFieldId ?? 0,
          CustomField: form.customField,
          ReportTypeName: form.reportTypeName,
        });
      }
      closeModal();
      setLoading(true);
      await loadConfigs();
    }
    catch
    {
      setModalError('Operation failed. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const isDuplicated = (model: any) =>
  {
    return configs.some(
      item => item.CustomFieldId == model.customFieldId && item.ReportTypeId == model.reportTypeId,
    );
  };

  const handleDelete = async () =>
  {
    if (!deleteTarget) return;
    setSaving(true);
    setModalError(null);
    try
    {
      await adminConsoleService.deleteReportConfiguration(deleteTarget.Id);
      closeModal();
      setLoading(true);
      await loadConfigs();
    }
    catch
    {
      setModalError('Operation failed. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#555] whitespace-nowrap">Find by keyword:</span>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search configurations..."
              className="border border-[#d0d0d0] rounded px-3 py-1 text-[12px] outline-none focus:border-[#62aacb] w-[220px]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#555]"
              >
                <span className="material-icons text-[13px]">close</span>
              </button>
            )}
          </div>
        </div>

        <button
          onClick={openAdd}
          className="flex items-center gap-1 text-[12px] text-[#5697b3] hover:text-[#3d7fa8] transition-colors"
        >
          <span className="material-icons text-[18px] dark:text-[#beb7ac]">add_circle_outline</span>
          Add Report Configuration
        </button>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-5 text-[12px] text-[#aaa]">
            <span className="material-icons animate-spin text-[16px] text-[#62aacb]">refresh</span>
            Loading…
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                <th className="text-left px-4 py-2 dark:text-[#beb7ac] font-medium">Report Type</th>
                <th className="text-left px-4 py-2 dark:text-[#beb7ac] font-medium">
                  Custom Detail
                </th>
                <th className="text-right px-4 py-2 dark:text-[#beb7ac] font-medium w-[130px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-[#aaa]">
                    {search ? 'No results match your search.' : 'No report configurations found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((c, i) => (
                  <tr
                    key={c.Id}
                    className={`border-b border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f0f6fb] transition-colors`}
                  >
                    <td className="px-4 py-2 text-[#5697b3]">{c.ReportTypeName}</td>
                    {/* <td className="px-4 py-2 text-[#555]">{c.ItemType}</td>
                  <td className="px-4 py-2 text-[#555]">{c.LayerName}</td> */}
                    <td className="px-4 py-2 text-[#555]">{c.CustomField}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(c)}
                          className="px-3 py-1 bg-[#62aacb] text-white rounded text-[11px] hover:bg-[#4e99ba] transition-colors dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDelete(c)}
                          className="px-3 py-1 bg-[#e57373] text-white rounded text-[11px] hover:bg-[#d32f2f] transition-colors dark:bg-[#8d312c] dark:hover:brightness-[0.85]"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {(modal === 'add' || modal === 'edit') && (
        <ConfigModal
          title={modal === 'add' ? 'Add Report Configuration' : 'Edit Report Configuration'}
          form={form}
          itemTypes={itemTypes}
          detail={detail}
          detailLoading={detailLoading}
          saving={saving}
          error={modalError}
          onReportTypeChange={(id, reportTypeName) =>
            setForm(prev => ({ ...prev, reportTypeId: id, reportTypeName: reportTypeName }))
          }
          onItemTypeChange={handleItemTypeChange}
          onLayerChange={handleLayerChange}
          onFormChange={patch => setForm(prev => ({ ...prev, ...patch }))}
          onConfirm={handleConfirm}
          onClose={closeModal}
        />
      )}

      {modal === 'delete' && deleteTarget && (
        <DeleteModal
          config={deleteTarget}
          saving={saving}
          error={modalError}
          onConfirm={handleDelete}
          onClose={closeModal}
        />
      )}
    </div>
  );
};
